import './Menu.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';

type Props = {
  title?: React.ReactNode;
  dots?: boolean;
  dataCy?: string;
  children: React.ReactNode;
};

export function Menu({ title = '', dataCy, dots, children }: Props) {
  const menuRef = useRef(null);
  const [isActive, setIsActive] = useDetectOutsideClick(menuRef, false);

  const onClick = useCallback(() => {
    setIsActive(true);
  }, [setIsActive]);

  return (
    <div className="menu-container" data-cy={dataCy}>
      <button onClick={onClick}>
        <span className={dots ? 'menu--vertical' : ''}>{dots ? '\u2026' : title}</span>
      </button>
      {isActive && (
        <nav ref={menuRef} className={`menu ${isActive ? 'active' : 'inactive'}`}>
          <ul>{children}</ul>
        </nav>
      )}
    </div>
  );
}

Menu.Item = ({ onSelect, dataCy, children }: { onSelect?: () => void; dataCy?: string; children: React.ReactNode }) => {
  return (
    <li data-cy={dataCy} onClick={onSelect}>
      {children}
    </li>
  );
};

const useDetectOutsideClick = (
  el: React.RefObject<HTMLElement | null>,
  initialState: boolean
): [boolean, (state: boolean) => void] => {
  const [isActive, setIsActive] = useState(initialState);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (el.current && !el.current.contains(event.target as Node)) {
        setIsActive(false);
      }
    };

    if (isActive) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', onClick);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', onClick);
      };
    }
  }, [isActive, el]);

  return [isActive, setIsActive];
};
